import React, { useCallback, useEffect, useRef, useState } from "react"
import { CSSTransition } from "react-transition-group"
import i18nData from "@rogue_4/locales/zh-CN.json"
import { PropsWithStyle, StyledComponent } from "@rogue_4/types"
import classNames from "classnames"

import { i18n } from "@/utils/i18n"
import { GAME_EQUIP_DICT, IGameEquipWithAction } from "../../../data/battle"
import { useGameEquip } from "../../../store"
import { IGameEquip } from "../../../type"
import { SFX } from "../../../utils/sound"
import { getTransitionClassnames } from "../../../utils/transition"
import Button from "../../Button"
import ModalTitle from "../common/modalTitle"
import { useGameEquipModal, useGameModalStore } from "../store"

import styles from "./index.module.less"

const EquipFrame = React.forwardRef<
    HTMLDivElement,
    PropsWithStyle<{
        equip?: IGameEquipWithAction
        locked?: boolean
        onButtonClick?: () => void
    }>
>(({ className, style, equip, locked, onButtonClick }, ref) => {
    const handleButtonClick = useCallback(
        (e: React.MouseEvent) => {
            if (!locked) {
                onButtonClick?.()
                e.stopPropagation()
            }
        },
        [locked, onButtonClick]
    )
    const callEquipModal = useGameEquipModal()
    const handleClick = useCallback(() => {
        if (equip) {
            callEquipModal(equip, true)
            SFX.play("commonClick")
        }
    }, [callEquipModal, equip])
    return (
        <div
            ref={ref}
            className={classNames(
                styles.equipFrame,
                !equip && styles.disabled,
                locked && styles.locked,
                className
            )}
            onClick={handleClick}
            style={style}
        >
            <div className={styles.bg}></div>
            <div
                className={styles.img}
                style={{ backgroundImage: `url(${equip?.image})` }}
            ></div>
            <div
                className={classNames(styles.type, equip && styles[equip.type])}
            ></div>
            <Button
                className={classNames(styles.button)}
                size="small"
                active={false}
                invert={locked}
                onClick={handleButtonClick}
            >
                {locked
                    ? i18n(i18nData, "rogue_4.game.useItem.locked")
                    : "Use Items"}
            </Button>
        </div>
    )
})
EquipFrame.displayName = "EquipFrame"

const UseItemModal: StyledComponent = ({ className, style }) => {
    const [active, setActive] = useState(false)
    const cbRef = useRef<(key: string) => void>()
    useEffect(() => {
        useGameModalStore.setState({
            callUseItemModal: (onUseItem?: (key: string) => void) => {
                setActive(true)
                cbRef.current = onUseItem
            },
        })
    }, [])
    const { consumeEquip, equipArr } = useGameEquip()
    const lockUseRef = useRef(false)
    const handleUseItem = useCallback(
        (index: number) => {
            if (lockUseRef.current) {
                return
            }
            lockUseRef.current = true
            SFX.play("commonClick")
            setActive(false)
            setTimeout(() => {
                cbRef.current?.(equipArr[index].key)
                consumeEquip(index)
                lockUseRef.current = false
            }, 300)
        },
        [consumeEquip, equipArr]
    )
    const handleClose = useCallback(() => {
        SFX.play("commonClick")
        setActive(false)
    }, [])
    return (
        <CSSTransition
            in={active}
            timeout={300}
            classNames={getTransitionClassnames(styles)}
        >
            <div
                className={classNames(styles.useItemModal, className)}
                style={style}
                onClick={(e) => {
                    e.stopPropagation()
                }}
            >
                <ModalTitle className={styles.title} brain>
                    {i18n(i18nData, "rogue_4.game.title.useItem")}
                </ModalTitle>
                <div className={styles.frameContainer}>
                    {equipArr[0] && (
                        <EquipFrame
                            equip={GAME_EQUIP_DICT[equipArr[0].key]}
                            locked={!equipArr[0].avai}
                            onButtonClick={() => handleUseItem(0)}
                        />
                    )}
                    {equipArr[1] && (
                        <EquipFrame
                            equip={GAME_EQUIP_DICT[equipArr[1].key]}
                            locked={!equipArr[1].avai}
                            onButtonClick={() => handleUseItem(1)}
                        />
                    )}
                    {equipArr[2] && (
                        <EquipFrame
                            equip={GAME_EQUIP_DICT[equipArr[2].key]}
                            locked={!equipArr[2].avai}
                            onButtonClick={() => handleUseItem(2)}
                        />
                    )}
                </div>
                <div className={styles.backButton} onClick={handleClose}></div>
            </div>
        </CSSTransition>
    )
}

export default UseItemModal
